
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form } from 'react-bootstrap'
import { FiCheck, FiX } from 'react-icons/fi';
import moment from 'moment';
// stores
import { useUserStore } from 'stores';
// api
import { getMateriasIbic } from 'api/company';
import { getBookBindings } from 'api/libros';
import { getAllAuthors } from 'api/products';
// components
import Input from 'components/backoffice/customInput';
import SearchEntity from '../customInput/SearchEntity';
import DatePicker from 'components/backoffice/dateTimePicker';
import SearchLanguage from '../customInput/SearchLanguage';
import CustomCheckbox from 'components/backoffice/customCheckbox';

const BookForm = ({ control, book, setValue }) => {
  const { t } = useTranslation(['backoffice']);
  const { isAdministrator, profile } = useUserStore();
  
  const { companyHasStock, stockEntries } = useMemo(() => {
    let companyHasStock = {};
    let stockEntries = [];
    
    if (book?.availabled_status) {
      let availabledStatus = book.availabled_status;
      
      if (typeof availabledStatus === 'string') {
        try {
          availabledStatus = JSON.parse(availabledStatus);
        } catch (e) {
          console.error('Error parsing availabled_status:', e);
          availabledStatus = [];
        }
      }
      
      if (Array.isArray(availabledStatus)) {
        availabledStatus.forEach(item => {
          if (item.code) {
            companyHasStock[item.code] = item.status === 2;
            stockEntries.push({
              code: item.code,
              name: item.name || item.code,
              hasStock: item.status === 2
            });
          }
        });
      } 
      else if (typeof availabledStatus === 'object') {
        companyHasStock = availabledStatus;
        stockEntries = Object.entries(availabledStatus).map(([code, hasStock]) => ({
          code,
          name: code,
          hasStock: hasStock === true || hasStock === 2
        }));
      }
    }
    
    return { companyHasStock, stockEntries };
  }, [book?.availabled_status]);
  
  const canEditStock = isAdministrator === true && profile === 'worker';
  
  const currentCompanyHasStock = useWatch({
    control,
    name: 'availabled_status',
    defaultValue: companyHasStock || {}
  });
  
  useEffect(() => {
    if (Object.keys(companyHasStock).length > 0 && setValue && (!currentCompanyHasStock || Object.keys(currentCompanyHasStock).length === 0)) {
      setValue('availabled_status', companyHasStock);
    }
  }, [companyHasStock, setValue, currentCompanyHasStock]);
 
  return (
    <Row>
      <Col md={4} className='mt-4'>
        <Form.Label className="text-light">{t('autores')}</Form.Label>
        <Controller
          control={control}
          name="authors"
          defaultValue={Array.isArray(book?.authors)
            ? book.authors.map(item => ({
              value: item.id,
              label: item.nombre_autor,
            }))
            : []}
          render={({ field }) => {
            return <SearchEntity
              {...field}
              fetchData={getAllAuthors}
              labelField="nombre_autor"
              valueField="id"
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('searchByAuthors')}
              isMulti={true}
              formatCustomLabel={true}
            />
          }

          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='languages'
          defaultValue={Array.isArray(book?.idioma)
            ? book.idioma.map(item => ({
              label: item.nombre,
              value: item.id,
            }))
            : []}
          render={({ field: { onChange, value } }) =>
            <SearchLanguage
              showLabel={t('language')}
              defaultValue={value}
              onSelected={(selected) => {
                onChange(selected);
              }}
              labelField="nombre"
              valueField="id"
            />
          }
        />
      </Col>
      <Col sm={4} className='mt-3'>
        <Controller
          name="publication_date"
          defaultValue={book?.publication_date === '2021-01-01' ? t('publicationDate') : book?.publication_date}
          control={control}
          render={({ field: { onChange, value } }) => (
             <DatePicker
              label={t('publicationDate')}
              className="input-control w-100 form-control"
              value={value ? moment(value).local().toDate() : null}
              onChange={onChange}
              maxDate={moment().toDate()}
              dateFormat="yyyy-MM-dd"
              showTimeInput={false}
              placeholderText={t('publicationDate')}
            />
          )}
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='num_paginas'
          defaultValue={book?.num_paginas}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('numPages')}
              placeholder={t('numPages')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='num_edicion'
          defaultValue={book?.num_edicion}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('numEdition')}
              placeholder={t('numEdition')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='alto_cm'
          defaultValue={book?.alto_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('height')}
              placeholder={t('height')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='ancho_cm'
          defaultValue={book?.ancho_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('width')}
              placeholder={t('width')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='grosor_cm'
          defaultValue={book?.grosor_cm}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('thickness')}
              placeholder={t('thickness')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className='mt-4'>
        <Controller
          control={control}
          name='peso'
          defaultValue={book?.peso}
          render={({ field: { onChange, value } }) =>
            <Input
              label={t('weight')}
              placeholder={t('weight')}
              type="number"
              value={value}
              onChange={(val) => {
                if (Number(val) < 0 || isNaN(Number(val))) {
                  onChange(0);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
            />
          }
        />
      </Col>
      <Col md={4} className="mt-4">
        <Form.Label className="text-light">{t('ibicSubjects')}</Form.Label>
        <Controller
          control={control}
          name="ibic"
          defaultValue={Array.isArray(book?.materia_ibic)
            ? book.materia_ibic.map(item => ({
              value: item.id,
              label: `${item.codigo} (${item.descripcion})`,
            }))
            : []}
          render={({ field }) => (
            <SearchEntity
              {...field}
              fetchData={getMateriasIbic}
              filterField="codigo,descripcion"
              labelField="codigo"
              valueField="id"
              additionalFields={['descripcion']}
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('ibicSubjects')}
              isMulti={true}
              formatCustomLabel={true}
            />
          )}
        />
      </Col>
      <Col md={4} className="mt-4 select">
        <Form.Label className="text-light">{t('bookBinding')}</Form.Label>
        <Controller
          control={control}
          name="bookbindings"
          defaultValue={Array.isArray(book?.encuadernacion)
            ? book.encuadernacion.map((item) => ({
              value: item.id,
              label: item.descripcion,
            }))
            : []}
          render={({ field }) => (
            <SearchEntity
              {...field}
              fetchData={getBookBindings}
              filterField="descripcion"
              labelField="descripcion"
              valueField="id"
              onSelected={(items) => field.onChange(items)}
              defaultValue={field.value}
              placeholder={t('bookBinding')}
              isMulti={true}
              formatCustomLabel={true}
            />
          )}
        />
      </Col>
      {stockEntries.length > 0 && (
        <Col md={4} className="my-4">
          <Form.Label className="text-light">{t('companyStockAvailability')}</Form.Label>
          {canEditStock ? (
            <Controller
              control={control}
              name="availabled_status"
              defaultValue={companyHasStock || {}}
              render={({ field: { onChange, value } }) => {
                const formValue = value || companyHasStock || {};
                return (
                  <div className="border border-secondary rounded p-3 bg-dark h-100">
                    <div className="d-flex flex-column gap-2">
                      {stockEntries.map(({ code, name }) => {
                        const currentValue = formValue[code] || false;
                        return (
                          <div className="d-flex justify-content-between align-items-center text-light" key={code}>
                            <span>{name}</span>
                            <CustomCheckbox
                              id={`stock-${code}`}
                              label={currentValue ? t('si') : t('no')}
                              checked={currentValue}
                              onChange={(e) => {
                                const newValue = { ...formValue, [code]: e.target.checked };
                                onChange(newValue);
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            <div className="border border-secondary rounded p-3 bg-dark h-100">
              <div className="d-flex flex-column gap-2">
                {stockEntries.map(({ code, name, hasStock }) => (
                  <div className="d-flex justify-content-between align-items-center text-light" key={code}>
                    <span>{name}</span>
                    <span className="d-flex align-items-center gap-2">
                      {hasStock ? <FiCheck className="text-success" /> : <FiX className="text-danger" />}
                      {hasStock ? t('si') : t('no')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
      )}
    </Row>
  );
};

BookForm.propTypes = {
  control: PropTypes.any,
  book: PropTypes.any,
  setValue: PropTypes.func,
};

export default BookForm;